import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { SlideTimerIndicator } from "@components/atoms/SlideTimerIndicator"
import { Button } from "@components/ui/Button"
import { Chip } from "@components/ui/Chip"
import { Select } from "@components/ui/Select"
import { FerveAPI } from "@features/ferve/ferve-api-client"
import { ItemPricing } from "@features/ferve/ferve-cart"
import { useFerve } from "@features/ferve/FerveProvider"
import { useFlyingCarpet } from "@features/flying-carpet/useFlyingCarpet"
import { trackEvent } from "@features/tracking/tracking"
import { useSlideTimer } from "@hooks/useSliderTimer"
import { formatDollars } from "@utils/money-format"
import { defineBlock, EditableText, InnerBlocks, useInlineEditableValue, usePostTitleEditor } from "eddev/blocks"
import { AnimatePresence, motion } from "motion/react"
import { useEffect, useRef, useState } from "react"

export const meta: BlockMeta = {
  title: "Merchandise",
  description: "A merch item",
  category: "embed",
  icon: "cart",
  postMetaBlock: {
    fieldName: "merchInfo",
    postTypes: ["merchandise"],
  },
}

export default defineBlock("merch/merchandise-item", (props) => {
  const carpet = useFlyingCarpet()
  const ferve = useFerve()
  const ref = useRef<HTMLDivElement>(null)
  const [pricing, setPricing] = useState<null | ItemPricing[]>(null)
  const [isAddingToCart, setIsAddingToCart] = useState(false)
  const [selectedItem, setSelectedItem] = useState<string | undefined>(undefined)

  const [title] = useInlineEditableValue<string>("title")

  const itemHash = props.merchandise?.info?.hash!

  async function addToCart() {
    setIsAddingToCart(true)
    try {
      await ferve.cart.addItemsToCart(itemHash!, [
        {
          type: {
            codeHash: item?.codeHash!,
            priceTypeHash: item?.priceTypeHash!,
          },
          quantity: 1,
        },
      ])
      carpet.launch("cart")
      trackEvent("Add Merch To Cart", {}, ref.current)
    } catch (err) {
    } finally {
      setIsAddingToCart(false)
    }
  }

  useEffect(() => {
    if (!itemHash) {
      setPricing(null)
      return
    }

    const gotPricing = (pricing: ItemPricing[]) => {
      console.log("Got pricing", pricing)
      setPricing(pricing)
      if (pricing.length === 1) {
        setSelectedItem(pricing[0].priceTypeHash)
      }
    }

    if (env.admin) {
      new FerveAPI("sff").cart.getPricing(itemHash).then(gotPricing)
    } else {
      ferve.cart
        .getPricing(itemHash!)
        .then((items) => items.filter((item) => !!item.label))
        .then(gotPricing)
    }
  }, [itemHash])

  const item = pricing?.find((item) => item.priceTypeHash === selectedItem)

  const chip2 = useInlineEditableValue("chip2", "")

  const timer = useSlideTimer({
    total: props.images?.length!,
    slideDuration: 5,
  })

  return (
    <div ref={ref} data-mixpanel-block="Merch Tile" data-mixpanel-product-title={title} className="flex flex-col">
      <div className="theme-light bg-orange text-black">
        {/* Image */}
        {props.images?.length! > 0 && (
          <div className="aspect-[4/3] relative theme-dark">
            <AnimatePresence>
              <motion.div
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                key={timer.current}
              >
                <ResponsiveImage {...props.images![timer.current]!} max />
              </motion.div>
            </AnimatePresence>
            {props.images?.length! > 1 && (
              <>
                <div className="absolute left-3 bottom-3 z-10">
                  <SlideTimerIndicator timer={timer} />
                </div>
              </>
            )}
          </div>
        )}

        {/* Info */}
        <div className="p-5">
          {/* Chips */}
          <div className="flex items-center gap-2 mb-4">
            <Chip variant="primary">
              <EditableText store="chip1" defaultValue="Merch" />
            </Chip>
            {env.admin ? (
              <Chip variant="inverted">
                <EditableText store={chip2} defaultValue="Extra Tag" />
              </Chip>
            ) : chip2[0] ? (
              <Chip variant="inverted">{chip2[0]}</Chip>
            ) : null}
          </div>

          {/* Title/price */}
          <div className="flex flex-col gap-2 mb-4">
            <EditableText
              store={usePostTitleEditor(props.merchandise?.title!)}
              as="h4"
              className="type-title-m"
              placeholder="Enter a title"
            />
            <EditableText
              store="pricing"
              as="div"
              className="type-label-caps"
              placeholder="Enter pricing, eg. $29 AUD inc. GST"
            />
          </div>

          {/* Description */}
          <div className="wysiwyg">
            <InnerBlocks allowedBlocks={["#richtext"]} />
          </div>

          {/* Add to Cart */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-3 mt-6">
            {pricing?.length! > 1 && (
              <Select
                value={selectedItem}
                onValueChange={setSelectedItem}
                items={pricing?.map((item) => ({ type: "item", label: item.label, value: item.priceTypeHash }))!}
                placeholder="Select size..."
              />
            )}
            <Button
              shape="branded"
              variant="primary"
              disabled={!item}
              onClick={addToCart}
              loading={isAddingToCart || !pricing}
            >
              {item && formatDollars(item?.price).replace(/\.00/, "") + " - "}
              Add to Cart
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
})
